import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Switch } from '../ui/switch';
import { api } from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { ProductIcon, AuroraLogo, OFFICE_APPS } from './icons';
import './office.css';

// Solo los tipos que Aurora IA sabe crear (doc, sheet y slides por /office/ai/create; form por /forms/ia)
const EXAMPLES = {
  form: ['Encuesta de satisfacción para clientes de un servicio de seguridad', 'Registro para un curso de primeros auxilios', 'Examen de 10 preguntas sobre protocolos de emergencia'],
  doc: ['Propuesta comercial para un servicio de limpieza de oficinas', 'Política de vacaciones para una empresa de 30 personas', 'Acta de la reunión mensual de ventas'],
  sheet: ['Presupuesto mensual de una cafetería', 'Inventario de papelería con precio, stock y total', 'Calendario de pagos a proveedores del trimestre'],
  slides: ['Resultados del trimestre para el equipo directivo', 'Capacitación de atención al cliente', 'Presentación de un nuevo producto'],
};

/** "Crear con Aurora IA" desde Drive: genera un Documento, Hoja o Presentación a partir de una descripción. */
export default function CreateWithAIDialog({ open, onOpenChange, folder = '/', onCreated }) {
  const [kind, setKind] = useState('doc');
  const [prompt, setPrompt] = useState('');
  const [cuestionario, setCuestionario] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const create = async (e) => {
    e?.preventDefault();
    if (!prompt.trim() || busy) return;
    setBusy(true);
    try {
      if (kind === 'form') {
        const { data } = await api.post('/forms/ia', { prompt: prompt.trim(), folder, cuestionario }, { timeout: 180000 });
        toast.success(`${data.archivo.name} creado con Aurora IA`);
        setPrompt('');
        onCreated?.(data.archivo);
        return;
      }
      const { data } = await api.post('/office/ai/create', { kind, prompt: prompt.trim(), folder }, { timeout: 180000 });
      toast.success(`${data.name} creado con Aurora IA`);
      setPrompt('');
      onCreated?.(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Aurora IA no pudo crear el archivo');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent
        className="max-w-xl rounded-3xl zo-create"
        data-testid="zo-create-dialog"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          inputRef.current?.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl"><AuroraLogo size={24} /> Crear con Aurora IA</DialogTitle>
        </DialogHeader>
        <form onSubmit={create} className="zo-create-body">
          <div className="zo-create-kinds" role="radiogroup" aria-label="Tipo de archivo">
            {Object.entries(OFFICE_APPS).filter(([id]) => EXAMPLES[id]).map(([id, app]) => (
              <button
                key={id}
                type="button"
                role="radio"
                aria-checked={kind === id}
                className={`zo-create-kind ${kind === id ? 'is-active' : ''}`}
                onClick={() => setKind(id)}
                disabled={busy}
              >
                <ProductIcon kind={id} size={30} />
                <span>{app.name}</span>
              </button>
            ))}
          </div>
          <textarea
            ref={inputRef}
            className="zo-create-input"
            rows={4}
            value={prompt}
            disabled={busy}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder={`Describe ${kind === 'doc' ? 'el documento' : kind === 'sheet' ? 'la hoja de cálculo' : kind === 'form' ? 'el formulario' : 'la presentación'} que necesitas…`}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) create(e);
            }}
          />
          {kind === 'form' && (
            <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--zo-text-2)' }}>
              <Switch checked={cuestionario} onCheckedChange={setCuestionario} disabled={busy} />
              Cuestionario con respuestas correctas y puntos
            </label>
          )}
          <div className="zo-create-examples">
            {EXAMPLES[kind].map((ex) => (
              <button key={ex} type="button" disabled={busy} onClick={() => setPrompt(ex)}>{ex}</button>
            ))}
          </div>
          <div className="zo-create-footer">
            <span className="zo-muted">{busy ? 'Aurora IA está creando tu archivo, tarda unos segundos…' : 'Se guardará en tu carpeta actual de Drive.'}</span>
            <button type="submit" className="zo-btn-primary" disabled={!prompt.trim() || busy}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <AuroraLogo size={16} mono />}
              Crear
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
